import { FC, useState } from 'react';
import { AssignmentMonitoringQuery } from '../../graphQLOperations/__generated__/AssignmentMonitoringQuery.graphql';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import { getAssignmentReport } from '../../graphQLOperations/Assignment';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { getLabelAndClass } from '../../businessLogicService/AssignmentsAux/getLabelAndClasses';
import { AssignmentReportKeyModal } from './AssignmentReportKeyModal';
import { isOnlyTargetAssignment } from '../../businessLogicService/AssignmentsAux/isOnlyTargetAssignment';
import { StudentNameLink } from '../common/StudentNameLink/StudentNameLink';
import { MaskedStudentData } from '../MaskedData/MaskedStudentData';
import { PiiTypes } from '../../analytics/dataMasking';

interface Props {
  queryReference: PreloadedQuery<AssignmentMonitoringQuery, Record<string, unknown>>;
  classIds?: string[];
}
interface StudentWithSkills {
  id: string;
  name: string;
  publicUserId: string;
  lastNameForOrder: string;
  skillIds: { skId: string; score?: number | null; submittedOn: Date | null }[];
}
export const AssignmentStudentsTable: FC<Props> = ({ queryReference, classIds }) => {
  const { assignment } = usePreloadedQuery<AssignmentMonitoringQuery>(getAssignmentReport, queryReference);
  const [visibleReportKey, setVisibleReportkey] = useState(false);
  const getListOfStudents = () => {
    const studentsIdsInQuery: StudentWithSkills[] = [];
    assignment?.studentSkillAssignments?.forEach(stAssignment => {
      if (!studentsIdsInQuery.some(item => item.id === stAssignment?.user?.id)) {
        studentsIdsInQuery.push({
          id: stAssignment?.user?.id || '',
          name: stAssignment?.user?.firstName + ' ' + stAssignment?.user?.lastName,
          publicUserId: stAssignment?.user?.publicUserId ?? '',
          skillIds: [],
          lastNameForOrder: stAssignment?.user?.lastName ?? '',
        });
      }
    });

    assignment?.studentSkillAssignments?.forEach(stAssignment => {
      const index = studentsIdsInQuery.findIndex(item => item.id === stAssignment?.user?.id || '');
      studentsIdsInQuery[index].skillIds = [
        ...studentsIdsInQuery[index].skillIds,
        {
          skId: stAssignment?.skill?.id || '',
          score: stAssignment?.skillAssessment?.score,
          submittedOn: stAssignment?.submittedOn,
        },
      ];
    });

    return studentsIdsInQuery.sort((a, b) =>
      a.lastNameForOrder.localeCompare(b.lastNameForOrder, undefined, { sensitivity: 'base' }),
    );
  };

  return (
    <div className="mt-3 p-3">
      <AssignmentReportKeyModal visible={visibleReportKey} setVisible={setVisibleReportkey} />
      <DataTable
        showGridlines
        value={getListOfStudents()}
        paginator
        paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        rows={10}
        rowsPerPageOptions={[5, 10, 25]}
      >
        <Column
          field="name"
          className="w-10rem"
          body={(row: StudentWithSkills) => (
            <StudentNameLink studentId={row.id} classIds={classIds}>
              <MaskedStudentData type={PiiTypes.FullName} student={row}>
                {row.name}
              </MaskedStudentData>
            </StudentNameLink>
          )}
          header={
            <div>
              <Button onClick={() => setVisibleReportkey(true)} outlined icon="" label="Report Key" />
            </div>
          }
        />
        <Column
          body={(element: StudentWithSkills) => (
            <div className="w-full">
              {element.skillIds.some(element => element.skId === assignment?.skill?.id) ? (
                getLabelAndClass(element.skillIds.find(item => item.skId === assignment?.skill?.id)!)
              ) : (
                <span
                  style={{ maxWidth: '25rem' }}
                  className="nc_skill-result-mini w-12 block text-center mx-auto font-bold not-assigned-span"
                >
                  Not assigned
                </span>
              )}
            </div>
          )}
          field="status"
          header={
            <div>
              <span className="text-muted border-1 p-1 border-round text-400">Target</span>
              <p className="font-semibold">
                {assignment?.skill?.name} {`(${assignment?.skill?.code})`}
              </p>
            </div>
          }
        />
        {!isOnlyTargetAssignment(
          assignment?.skill?.id,
          assignment?.studentSkillAssignments?.map(item => item?.skill?.id || ''),
        ) &&
          assignment?.skill?.skillAncestries?.map(supportSkill => (
            <Column
              key={supportSkill?.ancestorSkill?.id}
              body={(element: StudentWithSkills) => (
                <>
                  {element.skillIds.some(element => element.skId === supportSkill?.ancestorSkill?.id || '') ? (
                    getLabelAndClass(element.skillIds.find(item => item.skId === supportSkill?.ancestorSkill?.id)!)
                  ) : (
                    <span
                      style={{ maxWidth: '25rem' }}
                      className="nc_skill-result-mini w-12 block text-center mx-auto font-bold not-assigned-span"
                    >
                      Not assigned
                    </span>
                  )}
                </>
              )}
              field="status"
              header={
                <div key={supportSkill?.ancestorSkill?.id}>
                  <span className="text-muted border-1 p-1 border-round text-400">Support</span>
                  <p className="font-semibold">
                    {supportSkill?.ancestorSkill?.name} {`(${supportSkill?.ancestorSkill?.code})`}
                  </p>
                </div>
              }
            />
          ))}
      </DataTable>
    </div>
  );
};
